import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PageHeader } from "@/components/ui/page-header"
import { Package, DollarSign, BarChart3, ShoppingCart, PlusCircle, Tags, Clock, Search, Bell, LayoutDashboard, Settings } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useVendorProducts } from "@/hooks/useProducts"
import { useVendorOrders, useVendorAnalytics } from "@/hooks/useOrders"
import { OrderStatus } from "@/api/api-client"
import VendorInventory from "./VendorInventory"
import AddProductForm from "./AddProductForm"
import VendorOrders from "./VendorOrders"
import VendorPerformance from "./VendorPerformance"
import VendorOverview from "./VendorOverview"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"

interface VendorDashboardContentProps { }

const VendorDashboardContent: React.FC<VendorDashboardContentProps> = () => {
  const { user } = useAuth()
  const { data: products } = useVendorProducts()
  const { data: orders } = useVendorOrders()
  const { data: analytics } = useVendorAnalytics(30)
  const [activeTab, setActiveTab] = React.useState("overview")
  const [focusedProductId, setFocusedProductId] = React.useState<number | null>(null)
  const [focusedOrderId, setFocusedOrderId] = React.useState<number | null>(null)

  const handleNavigateToProduct = (id: number) => {
    setFocusedProductId(id)
    setActiveTab("inventory")
  }

  const handleNavigateToOrder = (id: number) => {
    setFocusedOrderId(id)
    setActiveTab("orders")
  }

  const handleTabChange = (tab: string) => {
    setFocusedProductId(null)
    setFocusedOrderId(null)
    setActiveTab(tab)
  }
  const { t } = useLanguage()

  const activeProductsCount = products?.filter((p) => (p.stockQuantity ?? 0) > 0).length ?? 0
  const pendingOrdersCount =
    orders?.filter(
      (o) => o.status !== OrderStatus.OrderComplete && o.status !== OrderStatus.OrderDeclined
    ).length ?? 0
  const totalSales =
    analytics?.totalRevenue ??
    orders
      ?.filter((o) => o.status === OrderStatus.OrderComplete)
      .reduce((acc, o) => acc + (o.totalAmount ?? 0), 0) ??
    0

  const text = t.vendorDashboard

  return (
    <div className="flex min-h-[calc(100vh-80px)] w-full bg-muted">
      {/* Sidebar */}
      <aside className="relative flex w-64 flex-shrink-0 flex-col border-r border-border bg-background py-8">
        <div className="mb-8 px-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-primary">{t.vendorDashboard.mainMenu}</h2>
        </div>
        <nav className="space-y-2 px-4">
          {[
            { id: "overview", label: text.overview || "Overview", icon: LayoutDashboard },
            { id: "inventory", label: text.tabs.inventory, icon: Package },
            { id: "orders", label: text.tabs.orders, icon: ShoppingCart },
            { id: "performance", label: text.performance, icon: BarChart3 },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors",
                activeTab === item.id
                  ? "bg-primary font-bold text-primary-foreground"
                  : "text-muted-foreground hover:bg-primary/50 hover:text-primary-foreground"
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto px-4 pb-4">
          <button className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-primary">
            <Settings className="h-4 w-4" />
            {text.settings || "Settings"}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {/* Top Header */}
        <header className="flex h-24 items-center justify-between border-b border-border bg-background px-8">
          <div>
            <h1 className="font-display text-4xl uppercase tracking-wide text-foreground">{text.title}</h1>
            <p className="text-sm text-muted-foreground">{text.welcome} {user?.displayName ?? "Vendor"}</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={text.tabs.orders ? `Search ${text.tabs.orders.toLowerCase()}...` : "Search orders..."}
                className="h-10 w-64 rounded-md border border-border bg-background pl-10 pr-4 text-sm outline-none transition-colors hover:border-accent focus:border-accent"
              />
            </div>
            <button className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:border-accent hover:text-accent">
              <Bell className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleTabChange("add")}
              className="flex h-10 flex-shrink-0 items-center gap-2 rounded bg-action px-5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <PlusCircle className="h-4 w-4" />
              {t.common.newProduct}
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-8">
          {activeTab === "overview" && (
            <VendorOverview
              onNavigateTab={handleTabChange}
              onNavigateToProduct={handleNavigateToProduct}
              onNavigateToOrder={handleNavigateToOrder}
            />
          )}
          {activeTab === "inventory" && (
            <div className="rounded-xl bg-background p-6 shadow-sm">
              <VendorInventory focusedProductId={focusedProductId} />
            </div>
          )}
          {activeTab === "performance" && (
            <div className="rounded-xl bg-background p-6 shadow-sm">
              <VendorPerformance />
            </div>
          )}
          {activeTab === "add" && (
            <div className="mx-auto max-w-4xl rounded-xl bg-background p-6 shadow-sm">
              <AddProductForm />
            </div>
          )}
          {activeTab === "orders" && (
            <div className="rounded-xl bg-background p-6 shadow-sm">
              <VendorOrders focusedOrderId={focusedOrderId} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default VendorDashboardContent

